import React from 'react';
import { EntryRowContext } from '../../../../core/components/InlineEditTable/types';
import { projectStatusOptions } from '../../models';
import TeamMemberInline from '../QuickTasks/components/TeamMemberInline';
import {
  ColumnOptions,
  EntryDueDate,
  InlineSelectMenu,
  PRIORITY_OPTIONS,
  renderPriorityPill,
  renderStatusPill,
} from './constants';

type QuickEntryRowProps = EntryRowContext & {
  options: ColumnOptions;
};

/**
 * The work-order data-entry row (the top row of the projects table). Renders the
 * cells for creating a new work order: due date, prefix, name, details, status,
 * priority, assignee, and the submit affordance. Rendered inside the table's
 * entry `<tr>` via `entryRow.render`.
 */
const QuickEntryRow = ({
  options,
  values,
  setValue,
  submit,
  handleKeyDown,
}: QuickEntryRowProps) => (
  <>
    <td>
      <EntryDueDate value={values.dueDate} onChange={(date) => setValue('dueDate', date)} />
    </td>
    <td>
      <select
        className="form-control"
        style={{ height: 30, padding: '2px 6px', minWidth: 90 }}
        value={values.prefix ?? ''}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => setValue('prefix', event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            submit();
          }
        }}
      >
        <option value="">—</option>
        {(options.prefixes || []).map((prefix) => (
          <option key={prefix.type} value={prefix.type}>
            {prefix.message}
          </option>
        ))}
      </select>
    </td>
    <td>
      <input
        type="text"
        className="form-control"
        style={{ height: 30, padding: '2px 6px' }}
        value={values.name ?? ''}
        placeholder={options.intl.formatMessage({
          id: 'newWorkOrderPlaceholder',
          defaultMessage: 'New work order…',
        })}
        onChange={(event) => setValue('name', event.target.value)}
        onKeyDown={handleKeyDown}
      />
    </td>
    <td>
      <input
        type="text"
        className="form-control"
        style={{ height: 30, padding: '2px 6px' }}
        value={values.description ?? ''}
        placeholder={options.intl.formatMessage({
          id: 'addInstructionsPlaceholder',
          defaultMessage: 'Add instructions…',
        })}
        onChange={(event) => setValue('description', event.target.value)}
        onKeyDown={handleKeyDown}
      />
    </td>
    <td>
      <InlineSelectMenu
        value={values.status || 'New'}
        options={projectStatusOptions}
        renderValue={(v) => renderStatusPill(v || 'New')}
        onSelect={(next) => setValue('status', next)}
      />
    </td>
    <td>
      <InlineSelectMenu
        value={values.priority || 'Medium'}
        options={PRIORITY_OPTIONS}
        renderValue={(v) => renderPriorityPill(v || 'Medium')}
        onSelect={(next) => setValue('priority', next)}
      />
    </td>
    <td>
      <TeamMemberInline
        rowData={{ _taskId: -1, employeeId: values.teamMember || '', label: '' }}
        employees={options.employees || []}
        intl={options.intl}
        onChange={(_rowData, nextValue) => setValue('teamMember', nextValue)}
      />
    </td>
    <td>
      {/* Date Created is set on the backend; in the entry row this is purely a submit affordance. */}
      <button
        type="button"
        className="button primary"
        style={{ height: 30, padding: '2px 12px' }}
        onClick={(event) => {
          event.stopPropagation();
          submit();
        }}
      >
        {options.intl.formatMessage({ id: 'submit' })}
      </button>
    </td>
  </>
);

export default QuickEntryRow;
